/**
 * What to tell the practitioner when a profile save fails.
 *
 * One failure deserves its own sentence: the whole profile is a single DynamoDB
 * item with a hard 400 KB ceiling, and My Markers is the only part of it that
 * grows on its own — saving a test adds a definition for every marker name the
 * practitioner has not defined yet, and nothing ever prunes them. A practice
 * with enough distinct marker names eventually crosses the line, and from then
 * on EVERY profile write fails: Settings, Profile, My Markers, the whole lot.
 * "Error saving settings." sends them hunting in the wrong place; this names the
 * cause and what to do about it.
 *
 * Everything else gets the caller's own wording, since the caller knows what the
 * practitioner was trying to save.
 */

/** Substrings DynamoDB and API Gateway use for a too-large item. */
const TOO_LARGE_SIGNS = [
  'item size has exceeded',
  'maximum allowed size',
  'request entity too large',
  'validationexception',
  'payload',
];

/** Every place an Amplify/axios error hides its message. */
function messagesIn(error: any): string {
  if (!error) {
    return '';
  }
  const response = error.response || {};
  const data = response.data || {};
  return [
    error.message,
    error.code,
    data.message,
    data.error,
    typeof data === 'string' ? data : '',
    response.status ? String(response.status) : '',
  ]
    .filter(Boolean)
    .join(' | ')
    .toLowerCase();
}

export function isProfileTooLargeError(error: any): boolean {
  const text = messagesIn(error);
  if (!text) {
    return false;
  }
  // 413 is the size refusal that arrives with no message body at all.
  if (/\b413\b/.test(text)) {
    return true;
  }
  return TOO_LARGE_SIGNS.some(sign => text.includes(sign));
}

export const PROFILE_TOO_LARGE_MESSAGE =
  'Your account has more saved data than one record can hold, which is why this ' +
  'will not save. My Markers is usually the cause: it gains a definition for ' +
  'every new marker name entered on a test, so it grows on its own. Open ' +
  'My Markers under the Test Results menu and delete the definitions you no ' +
  'longer use, then try again. Tests you have already saved keep their own ' +
  'copies and are not affected.';

/**
 * The message to show for a failed profile save: the size explanation when that
 * is what happened, otherwise the caller's own fallback.
 */
export function profileSaveErrorMessage(error: any, fallback: string): string {
  return isProfileTooLargeError(error) ? PROFILE_TOO_LARGE_MESSAGE : fallback;
}
